import { type Map as MaplibreMap } from "maplibre-gl";

import { COLOR_KEYS, COLORS } from "@/constants/colors";
import { DEFAULT_STEP } from "@/constants/dataset";
import { DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";

import { generateColorRangesUniqueStops } from "../common";
import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";

export const addGridOrdinalLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataset: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map || !dataset.sublayers) return;

  const colorRanges = style.colorRanges as { value: string; color: string }[];

  // Construct the match expression with unique, sorted values
  // const matchExpression =;

  // Add fill layer
  map.addLayer({
    id: dataset.sublayers.fill,
    type: "fill",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "fill-color": [
        "case",
        ["has", category],
        [
          "match",
          ["get", category],
          colorRanges[0].value ?? "",
          COLORS[style.colorFamily][COLOR_KEYS[0]],
          ...(colorRanges
            .slice(1)
            ?.flatMap((range, index) => [
              range.value ?? "",
              COLORS[style.colorFamily][COLOR_KEYS[index + 1]]
            ]) ?? []),
          COLORS[style.colorFamily][COLOR_KEYS[colorRanges.length - 1]]
        ],
        "#CECECE"
      ],
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.8,
        style?.fillOpacity ?? 0.7
      ]
    }
  });

  // Add outline layer
  map.addLayer({
    id: dataset.sublayers.outline,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        "#FFCF01", // Visible outline when selected
        "#ffffff" // No outline when not selected
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        2, // Wider outline when selected
        1 // No outline when not selected
      ]
    }
  });
  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataset, style);
  addRankLabelLayer(isRankingLabelEnabled, map, dataset, style);
};

export const addGridQuantileLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: MaplibreMap | null,
  dataset: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map || !dataset.sublayers) return;

  const uniqueRanges = generateColorRangesUniqueStops(
    style.colorRanges,
    style.colorFamily,
    style.colorSteps || DEFAULT_STEP
  );

  // Ensure we have at least two ranges for a proper step expression
  // If we have 0 or 1 range, we can't create a valid step expression
  if (uniqueRanges.length < 2) {
    // If no ranges or just one range, use a default or the single color
    const fillColor =
      uniqueRanges.length === 1
        ? uniqueRanges[0][1]
        : COLORS[style.colorFamily][COLOR_KEYS[0]];

    map.addLayer({
      id: dataset.sublayers.fill,
      type: "fill",
      source: dataset.id,
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "fill-color": [
          "case",
          ["has", category],
          fillColor,
          "#CECECE" // Default fallback color
        ],
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          0.8,
          style?.fillOpacity || 0.7
        ]
      }
    });
  } else {
    // We have at least 2 ranges, so we can create a valid step expression
    map.addLayer({
      id: dataset.sublayers.fill,
      type: "fill",
      source: dataset.id,
      layout: {
        visibility: style?.visibility || "visible"
      },
      paint: {
        "fill-color": [
          "case",
          ["has", category],
          [
            "step",
            ["get", category],
            uniqueRanges[0][1], // Default color for values below first threshold
            uniqueRanges[1][0],
            uniqueRanges[1][1], // First threshold and color
            ...uniqueRanges.slice(2).flatMap(([value, color]) => [value, color])
          ],
          "#CECECE" // Default color for when property doesn't exist
        ],
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          0.8,
          style?.fillOpacity || 0.7
        ]
      }
    });
  }

  // Add outline layer
  map.addLayer({
    id: dataset.sublayers.outline,
    type: "line",
    source: dataset.id,
    layout: {
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        "#FFCF01", // Visible outline when selected
        "#ffffff" // No outline when not selected
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        2, // Wider outline when selected
        1 // No outline when not selected
      ]
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, dataset, style);
  addRankLabelLayer(isRankingLabelEnabled, map, dataset, style);
};
